import { optionBelongsToKind } from '@sb/integrations/utils';
import { makeNamespacedLog } from '@sb/log';
import type { ArmJointPositions, TCPOffsetOption } from '@sb/motion-planning';
import type { RoutineRunnerState, Space } from '@sb/routine-runner';
import { activeTooltipForFrame } from '@sb/routine-runner';
import type { RoutineRunnerHandle } from '@sbrc/services';

const log = makeNamespacedLog('remote-control.getRobotPosition');

/**
 * The vizbot can surface `jointAngles` as a non-array value (a typed array,
 * or a plain object with numeric keys `0..5` produced by a state-clone round
 * trip). Either form serializes over the wire as `{"0": ...}` instead of
 * `[...]`, which fails the `Space.Position` zod tuple schema when the client
 * reconstructs the routine — so `convertRoutineResponse` silently drops any
 * space item containing such a position.
 *
 * Normalize to a plain 6-tuple here so the payload round-trips correctly.
 */
function toPlainArray(angles: ArmJointPositions): ArmJointPositions {
  if (Array.isArray(angles)) {
    return angles;
  }

  const anyAngles = angles as unknown as ArrayLike<number> &
    Record<number, number>;

  // Typed arrays and array-likes with a numeric `length` work via Array.from.
  if (typeof anyAngles.length === 'number') {
    return Array.from(anyAngles) as unknown as ArmJointPositions;
  }

  // Plain object with numeric keys — read 0..5 directly.
  return [
    anyAngles[0],
    anyAngles[1],
    anyAngles[2],
    anyAngles[3],
    anyAngles[4],
    anyAngles[5],
  ] as unknown as ArmJointPositions;
}

/**
 * A pose is only usable if every component is a finite number. When the arm
 * has never been energized (e.g. service restarted and power not yet enabled),
 * the routine runner reports a `tooltipPoint` whose components are all `null`.
 * Such a pose must not be surfaced as a `Space.Position`, since it would fail
 * the `CartesianPose` zod schema and crash any consumer that parses it.
 */
function isPoseFinite(pose: Space.Position['pose']): boolean {
  if (pose == null) {
    return false;
  }

  const { x, y, z, w, i, j, k } = pose;

  return [x, y, z, w, i, j, k].every((value) => Number.isFinite(value));
}

/**
 * The equipment row whose tooltip the pose is being taught at, or `undefined`
 * when no single row can be named for it — in which case the pose carries no
 * id and the routine runner resolves it by option name against enabled
 * equipment, the same resolution its `pose` value was computed under.
 *
 * The Active Tooltip decides while it names the frame being captured AND names
 * the equipment defining it: that (option, `gripperID`) pair is the one the
 * routine runner resolves poses through, so a pose stamped with that id
 * re-resolves to the tip it was taught at — including a gripper that is
 * currently disabled, or one of two rows sharing a kind.
 *
 * An Active Tooltip carrying no id names an option only, which is what the
 * name fallback below already answers. It therefore falls through rather than
 * suppressing that fallback: a single owner is still unambiguous, and
 * answering `undefined` here would drop an id the pose could have carried.
 *
 * The two-owner suppression in the name fallback counts on `equipmentState`
 * holding enabled rows only — a caller-enforced feed (with a brief window
 * after a disable), not a structural guarantee of the type.
 */
function resolveGripperID(
  routineRunnerState: Pick<
    RoutineRunnerState,
    'configuration' | 'equipmentState'
  >,
  tcpOption: TCPOffsetOption,
): string | undefined {
  const activeTooltip = activeTooltipForFrame(
    routineRunnerState.configuration,
    tcpOption,
  );

  if (activeTooltip?.gripperID) {
    // The UI displays a pose's tooltip by NAME everywhere; these logs are
    // the one observable trace of the identity the pose will resolve
    // through. Debug level: live previews resolve on every render, so
    // anything louder reads as a stream of save events that never happened.
    log.debug('resolveGripperID', 'Pose frame resolves from Active Tooltip', {
      tcpOption,
      gripperID: activeTooltip.gripperID,
    });

    return activeTooltip.gripperID;
  }

  // `equipmentState` rows carry only `{id, kind, state}` — no device
  // configuration — so the owning row is matched by kind against the tooltip
  // names that kind publishes. Equipment the option does not name (a
  // force/torque sensor between the flange and the gripper) does not own the
  // tooltip and must not be stamped, however early it is listed.
  const owners = routineRunnerState.equipmentState.filter((device) =>
    optionBelongsToKind(tcpOption, device.kind),
  );

  if (owners.length === 1) {
    // The UI displays a pose's tooltip by NAME everywhere; this log is the
    // one observable trace of the identity the pose will resolve through.
    // Debug level: live previews resolve on every render, so anything louder
    // reads as a stream of save events that never happened.
    log.debug('resolveGripperID', 'Pose frame resolves to tooltip owner', {
      tcpOption,
      gripperID: owners[0].id,
      kind: owners[0].kind,
    });

    return owners[0].id;
  }

  if (owners.length > 1) {
    log.debug(
      'resolveGripperID',
      'Tooltip option owned by several rows; pose resolves name-only',
      { tcpOption, ownerIDs: owners.map((owner) => owner.id) },
    );
  }

  // Two rows of the same kind both contribute their offsets under name
  // resolution, which is how this pose was computed; stamping either id
  // would claim a tip only one of them defines.
  return undefined;
}

export const getRobotPosition = (
  routineRunnerHandle: RoutineRunnerHandle,
): Space.Position | null => {
  const routineRunnerState = routineRunnerHandle.getState();

  if (!routineRunnerState) {
    return null;
  }

  // No trustworthy live pose (e.g. arm powered off / not yet reported). Return
  // `null` so consumers fall back to their "no position" handling rather than
  // parsing a structurally-invalid pose. See Asana SUP-4073.
  if (!isPoseFinite(routineRunnerState.kinematicState.tooltipPoint)) {
    return null;
  }

  const tcpOption = routineRunnerState.kinematicState.tcpOffsetOption;

  const gripperID = resolveGripperID(routineRunnerState, tcpOption);

  return {
    jointAngles: toPlainArray(routineRunnerState.kinematicState.jointAngles),
    pose: routineRunnerState.kinematicState.tooltipPoint,
    tcpOption,
    gripperID,
    wristPose:
      routineRunnerState.kinematicState.tcpOffsetOption === 'wrist'
        ? undefined
        : routineRunnerState.kinematicState.wristPose,
  };
};
